<div class="firmware-page">
    <!-- Loading State -->
    @if (loading()) {
        <div class="grid">
            <div class="col-12 lg:col-5">
                <app-section-card class="h-full" title="Current Firmware" icon="pi pi-microchip">
                    <p-skeleton width="60%" height="1.5rem" class="mb-3" />
                    <p-skeleton width="40%" height="1rem" class="mb-2" />
                    <p-skeleton width="80%" height="1rem" />
                </app-section-card>
            </div>
            <div class="col-12 lg:col-7">
                <app-section-card class="h-full" title="Available Updates" icon="pi pi-arrow-up" tone="success">
                    <p-skeleton width="50%" height="1.5rem" class="mb-3" />
                    <p-skeleton width="100%" height="3rem" />
                </app-section-card>
            </div>
        </div>
    }

    <!-- Error State -->
    @if (error(); as err) {
        <p-message severity="error" class="mb-3">{{ err }}</p-message>
    }

    <!-- Main Content -->
    @if (!loading() && !error()) {
        <div class="grid">
            <!-- Current Firmware Card -->
            <div class="col-12 lg:col-5">
                <app-section-card class="h-full" title="Current Firmware" [subtitle]="deviceModel() || 'Unknown Model'" icon="pi pi-microchip">
                    <div class="current-firmware-content">
                        <div class="version-display">
                            <span class="version-number">{{ currentVersion() || 'Unknown' }}</span>
                            @if (isUpToDate()) {
                                <p-tag value="Up to date" severity="success" icon="pi pi-check" />
                            } @else if (latestAvailable()) {
                                <p-tag value="Update available" severity="warn" icon="pi pi-arrow-up" />
                            }
                        </div>
                        @if (hasPendingUpdate()) {
                            <div class="pending-badge">
                                <i class="pi pi-spin pi-spinner"></i>
                                <span>Firmware update pending</span>
                            </div>
                        }
                    </div>
                </app-section-card>
            </div>

            <!-- Quick Action Card -->
            <div class="col-12 lg:col-7">
                <app-section-card
                    class="h-full"
                    title="Available Updates"
                    [subtitle]="availableUpdates().length + ' update' + (availableUpdates().length !== 1 ? 's' : '') + ' available'"
                    icon="pi pi-arrow-up"
                    tone="success"
                >
                    <div actions class="header-actions">
                        <p-button icon="fa fa-refresh" [text]="true" severity="info" size="small" ariaLabel="Refresh firmware" pTooltip="Refresh firmware" (onClick)="refresh()" />
                    </div>
                    @if (isUpToDate()) {
                        <div class="up-to-date-message">
                            <i class="pi pi-verified"></i>
                            <div>
                                <strong>You're running the latest firmware</strong>
                                <p>No updates are available for this device.</p>
                            </div>
                        </div>
                    } @else if (firmwareList().length === 0) {
                        <div class="no-firmware-message">
                            <i class="pi pi-info-circle"></i>
                            <div>
                                <strong>No firmware packages found</strong>
                                <p>No firmware has been uploaded for this device model yet.</p>
                            </div>
                        </div>
                    } @else {
                        <div class="firmware-list">
                            @for (fw of firmwareList(); track fw.id) {
                                <div class="firmware-item" [class.current]="fw.isCurrent">
                                    <div class="firmware-item-left">
                                        <div class="firmware-version-row">
                                            <span class="fw-version">v{{ fw.version }}</span>
                                            @if (fw.isCurrent) {
                                                <p-tag value="Installed" severity="info" class="ml-2" />
                                            } @else if (fw.isUpgrade) {
                                                <p-tag value="Upgrade" severity="success" class="ml-2" />
                                            }
                                        </div>
                                        <div class="firmware-meta">
                                            <span class="fw-name">{{ fw.name }}</span>
                                            <span class="fw-separator">·</span>
                                            <span class="fw-size">{{ formatFileSize(fw.fileSizeBytes) }}</span>
                                            <span class="fw-separator">·</span>
                                            <span class="fw-date">{{ formatDate(fw.uploadedUtc) }}</span>
                                        </div>
                                        @if (fw.description) {
                                            <div class="firmware-description">{{ fw.description }}</div>
                                        }
                                    </div>
                                    <div class="firmware-item-right">
                                        @if (!fw.isCurrent) {
                                            <button pButton type="button"
                                                [class]="fw.isUpgrade ? 'p-button-sm' : 'p-button-sm p-button-secondary'"
                                                (click)="openUpdateDialog(fw)"
                                                [disabled]="hasPendingUpdate()"><i [class]="fw.isUpgrade ? 'pi pi-arrow-up' : 'pi pi-replay'" class="p-button-icon" aria-hidden="true"></i><span class="p-button-label">{{ fw.isUpgrade ? 'Update' : 'Reinstall' }}</span>
                                            </button>
                                        } @else {
                                            <button pButton type="button"
                                                class="p-button-sm p-button-outlined"
                                                (click)="openUpdateDialog(fw)"
                                                [disabled]="hasPendingUpdate()"><i class="pi pi-replay p-button-icon" aria-hidden="true"></i><span class="p-button-label">Reinstall</span>
                                            </button>
                                        }
                                    </div>
                                </div>
                            }
                        </div>
                    }
                </app-section-card>
            </div>

            <!-- Recent Activity -->
            @if (recentActions().length > 0) {
                <div class="col-12">
                    <app-section-card title="Update History" subtitle="Recent firmware update actions" icon="pi pi-history" tone="info" [count]="recentActions().length">
                        <div class="history-list">
                            @for (action of recentActions(); track action.id) {
                                <div class="history-item">
                                    <div class="history-icon-wrapper">
                                        <i [class]="statusIcon(action.status)"></i>
                                    </div>
                                    <div class="history-content">
                                        <div class="history-title">
                                            <span>Firmware Update</span>
                                            <p-tag [value]="action.status" [severity]="statusSeverity(action.status)" class="ml-2" />
                                        </div>
                                        <div class="history-meta">
                                            @if (action.payload?.version) {
                                                <span>v{{ action.payload.version }}</span>
                                                <span class="fw-separator">·</span>
                                            }
                                            <span>{{ formatDateTime(action.createdUtc) }}</span>
                                            @if (action.resultMessage) {
                                                <span class="fw-separator">·</span>
                                                <span class="history-message">{{ action.resultMessage }}</span>
                                            }
                                        </div>
                                    </div>
                                </div>
                            }
                        </div>
                    </app-section-card>
                </div>
            }
        </div>
    }
</div>

<!-- Update Confirmation Dialog -->
<p-dialog
    header="Schedule Firmware Update"
    [visible]="showUpdateDialog()"
    (visibleChange)="showUpdateDialog.set($event)"
    [modal]="true"
    [closable]="!submitting()"
    [style]="{width: '30rem'}">

    @if (selectedFirmware(); as fw) {
        <div class="update-dialog-content">
            <div class="update-summary">
                <div class="update-arrow">
                    <div class="update-from">
                        <span class="label">Current</span>
                        <span class="value">v{{ currentVersion() || '?' }}</span>
                    </div>
                    <i class="pi pi-arrow-right"></i>
                    <div class="update-to">
                        <span class="label">{{ fw.isCurrent ? 'Reinstall' : 'Target' }}</span>
                        <span class="value">v{{ fw.version }}</span>
                    </div>
                </div>
            </div>

            <div class="update-details">
                <div class="detail-row">
                    <span class="detail-label">File</span>
                    <span class="detail-value">{{ fw.fileName }}</span>
                </div>
                <div class="detail-row">
                    <span class="detail-label">Size</span>
                    <span class="detail-value">{{ formatFileSize(fw.fileSizeBytes) }}</span>
                </div>
            </div>

            <div class="schedule-section">
                <div class="schedule-toggle">
                    <button pButton type="button"
                        class="p-button-text p-button-sm"
                        (click)="scheduling.set(!scheduling()); scheduleDate.set(null)"><i [class]="scheduling() ? 'pi pi-bolt' : 'pi pi-calendar'" class="p-button-icon" aria-hidden="true"></i><span class="p-button-label">{{ scheduling() ? 'Dispatch immediately' : 'Schedule for later' }}</span>
                    </button>
                </div>
                @if (scheduling()) {
                    <p-datepicker
                        [ngModel]="scheduleDate()"
                        (ngModelChange)="scheduleDate.set($event)"
                        [showTime]="true"
                        [minDate]="minScheduleDate"
                        [stepMinute]="5"
                        appendTo="body"
                        placeholder="Select date and time"
                        class="w-full mt-2"
                        [inline]="true" />
                }
            </div>

            @if (submitError(); as err) {
                <p-message severity="error" class="mt-3">{{ err }}</p-message>
            }

            <p-message severity="warn" class="mt-3">The device will download the firmware and reboot to apply the update. This process cannot be cancelled once started.</p-message>
        </div>

        <div class="flex gap-2 justify-end mt-4">
            <button pButton type="button" class="p-button-text" (click)="showUpdateDialog.set(false)" [disabled]="submitting()"><span class="p-button-label">Cancel</span></button>
            <button pButton type="button"
                (click)="confirmUpdate()"
                [disabled]="submitting() || (scheduling() && !scheduleDate())"><i [class]="submitting() ? 'pi pi-spin pi-spinner' : (scheduling() ? 'pi pi-calendar-plus' : 'pi pi-arrow-up')" class="p-button-icon" aria-hidden="true"></i><span class="p-button-label">{{ scheduling() ? 'Schedule Update' : 'Update Now' }}</span>
            </button>
        </div>
    }
</p-dialog>
